import { AppDispatch } from "../../store";
import { checkGroupBooking, checkGroupBookingWithoutPNR } from "@/dataLayer/groupBooking";
import { initiateGroupBooking, reset, setGroupBoardingPass, setPrimaryContact, setGroupBooking, setJourneysInfo, setUserInputs } from "../../store/slices/groupBooking";
import { displayToastNotification } from "@/utils/helperFunctions/displayToastNotification";
import { BOOKING_CLASS, BOOKING_IS_ETIHAD_BOOKING, BOOKING_MODE, NOTIFICATION_MESSAGE_TYPE, TYPE_OF_FLOW_IN_GROUP } from "constants/variables";
import { extractResource } from "helper/extractResource";
import { resourceData } from "mock/resourceData";

// Define the interface for PNR group booking details
interface GroupBookingDetails {
  pnrDetails: string;
  lastName?: string;
  email?: string;
  bookingType?: string;
  dispatch: AppDispatch,
}

const toastNotification = (toasterType: string) => displayToastNotification(NOTIFICATION_MESSAGE_TYPE.ERROR, toasterType);

export const handleGroupBooking = async ({
  pnrDetails,
  lastName,
  email,
  bookingType,
  dispatch
}: GroupBookingDetails, resetRedux: Boolean = true) => {

  const paramGroupBooking = {
    pnrDetails: pnrDetails,
    lastName,
    email,
    bookingType: bookingType || TYPE_OF_FLOW_IN_GROUP.MY_BOOKING,
  };

  try {
    //reset and initiate group booking store
    if(resetRedux){
      dispatch(initiateGroupBooking());
      dispatch(reset());
    }
    // API Call
    const checkGroupBookingResponse = await checkGroupBooking(paramGroupBooking);
    dispatch(setUserInputs({ userInputs: paramGroupBooking }));

    // Handle Error Response
    if (checkGroupBookingResponse === false) {
      toastNotification(extractResource(resourceData, "noBookingFound"));
      return false; // Indicate failure
    }
    
    //Check if its Etihad Booking
    if (checkGroupBookingResponse === BOOKING_IS_ETIHAD_BOOKING) {
      return checkGroupBookingResponse;
    }

    // Check if the response is an object
    if (checkGroupBookingResponse && typeof checkGroupBookingResponse === "object") {
      if (checkGroupBookingResponse.typeOfBooking === BOOKING_MODE.IS_NOT_GROUP_BOOKING) {
        dispatch(setGroupBoardingPass({ groupBoardingPass: false }));
        sessionStorage.setItem("groupBooking", "false");
        return BOOKING_CLASS.NORMAL_BOOKING; // Indicate no group booking found
      }

      // Check if booking type is a group booking
      if (checkGroupBookingResponse.typeOfBooking === BOOKING_MODE.IS_GROUP_BOOKING) {
        const isGroupBooking = true;

        // Dispatch and update states
        sessionStorage.setItem("groupBooking", isGroupBooking.toString());
        dispatch(setGroupBooking({ groupBooking: isGroupBooking }));
        dispatch(setJourneysInfo({ journeysInfo: checkGroupBookingResponse.journeys}));
        dispatch(setPrimaryContact({ isPrimaryContact: checkGroupBookingResponse.isPrimaryContactMatched }));
        return true; // Indicate success
      }
    }
  } catch (error) {
    console.error("Error handling group booking:", error);
    toastNotification("Something went wrong, Please try again");
    return false; // Indicate failure
  }
};

// Define the interface for PNR group booking details
interface GroupBookingDetailsWithPnr {
  phone: string;
  departureDate: string;
  originStationCode?: string;
  destinationStatusCode?: string;
  bookingType?: string;
  dispatch: AppDispatch,
}

export const handleGroupBookingWithoutPNR = async ({
  phone,
  departureDate,
  originStationCode,
  destinationStatusCode,
  bookingType,
  dispatch,
  }: GroupBookingDetailsWithPnr) => {

  const paramGroupBooking = {
    phone: phone,
    departureDate: departureDate.replace(/\//g, "-"),
    destinationStatusCode: destinationStatusCode,
    originStationCode: originStationCode,
    bookingType: bookingType || TYPE_OF_FLOW_IN_GROUP.MY_BOOKING,
  };

  try {
    //reset and initiate group booking store
    dispatch(initiateGroupBooking());
    dispatch(reset());

    // API Call
    const checkGroupBookingResponse = await checkGroupBookingWithoutPNR(paramGroupBooking);

    // Handle error response
    if (checkGroupBookingResponse === false) {
      toastNotification(extractResource(resourceData, "noBookingFound"));
      return false; // Indicate failure
    }

    //Check if its Etihad Booking
    if (checkGroupBookingResponse === BOOKING_IS_ETIHAD_BOOKING) {
      return checkGroupBookingResponse;
    }

    // Check if the response is valid and contains group booking information
    if (checkGroupBookingResponse && typeof checkGroupBookingResponse === "object") {
      if (checkGroupBookingResponse.typeOfBooking === BOOKING_MODE.IS_NOT_GROUP_BOOKING) {
        dispatch(setGroupBoardingPass({ groupBoardingPass: false }));
        sessionStorage.setItem("groupBooking", "false");
        return BOOKING_CLASS.NORMAL_BOOKING; // Indicate no group booking found
      }

      if (checkGroupBookingResponse.typeOfBooking === BOOKING_MODE.IS_GROUP_BOOKING) {
        const isGroupBooking = true;

        // Update state and session storage
        dispatch(
          setUserInputs({
            userInputs: { ...paramGroupBooking, pnrDetails: checkGroupBookingResponse?.recordLocator || "" },
          })
        );
        sessionStorage.setItem("groupBooking", isGroupBooking.toString());
        dispatch(setGroupBooking({ groupBooking: isGroupBooking }));
        return true; // Indicate success
      }
    }
  } catch (error) {
    console.error("Error handling group booking:", error);
    toastNotification("Something went wrong, Please try again");
    return false; // Indicate failure
  }
};